import React, { useMemo, useRef, useState } from 'react';
import CardableTable from './CardableTable';
import VenueActivityFeed from './VenueActivityFeed';
import UserAvatar from '../ui/UserAvatar';
import './RecordHistoryTab.css';
import type { AuditLogRow } from '../data/auditLog';
import { auditActorLabel, isAuditRowUndoable, type VenueHistoryFilters } from '../data/auditLog';

export type FieldChange = { key: string; before: any; after: any };
export type DisplayItem = { row: AuditLogRow; changes: FieldChange[] };

export interface RecordHistoryTabProps {
  auditRows: AuditLogRow[];
  loading: boolean;
  error?: string;
  extraDisplayItems?: DisplayItem[];
  extraFilterOptions?: { value: string; label: string }[];
  filterPredicate?: (item: DisplayItem, filterValue: string) => boolean;
  tableToTab?: (tableName: string) => string;
  resolveFieldLabel?: (key: string, row: AuditLogRow) => string;
  storageKey: string;
  canUndo: boolean;
  undoMutation: { mutate: (row: AuditLogRow) => void; isPending: boolean };
  userNames: Record<string, string>;
  userHeadshots?: Record<string, string>;
  renderRowAction?: (item: DisplayItem) => React.ReactNode;
  resolveRowChanges?: (row: AuditLogRow) => FieldChange[] | undefined;
  renderFieldValue?: (value: unknown, key: string, row: AuditLogRow) => React.ReactNode;
  formatFieldPreview?: (value: unknown, key: string) => string;
  title?: string;
  onFiltersChange?: (filters: VenueHistoryFilters) => void;
  tableOptions?: { value: string; label: string }[];
  actionOptions?: string[];
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  presentation?: 'table' | 'venue';
  refreshing?: boolean;
  onRetry?: () => void;
}

const IGNORE_KEYS = new Set(['id', 'updated_at', 'version', 'ui_prefs', '__payload_mode', '__compacted_reason', 'changed_keys', 'business_changed_keys']);
const IGNORE_ON_INSERT_GLOBAL = new Set([
  'id', 'created_at', 'created_by', 'updated_at', 'updated_by',
  'deleted_at', 'deleted_by', 'version', 'order_index', 'position',
  'venue_id', 'event_id', 'client_id', 'contact_id', 'record_id', 'av_company_id',
  'venue_room_id', 'venue_note_id', 'matched_venue_id', 'event_room_id', 'event_venue_id', 'milestone_id',
]);
const IGNORE_ON_INSERT_BY_TABLE: Record<string, Set<string>> = {
  'venue_logistics': new Set(['key', 'title', 'kind', 'venue_id', 'order_index']),
};
const TYPING_MERGE_MS = 2000;

function humanizeField(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\bid\b/gi, 'ID')
    .replace(/\burl\b/gi, 'URL')
    .replace(/\bav\b/gi, 'AV')
    .replace(/\bsqft\b/gi, 'sq ft')
    .replace(/^\w/, (c) => c.toUpperCase());
}

function defaultTableToTab(tableName: string): string {
  return tableName
    .replace(/^venue_|^client_|^event_/, '')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

function safeKeys(o: any): string[] { return o && typeof o === 'object' ? Object.keys(o) : []; }
function isEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}

function computeFieldChanges(r: AuditLogRow): FieldChange[] {
  if (r.action === 'DELETE') return [];
  const oldObj = r.old_data || {};
  const newObj = r.new_data || {};
  const keys = Array.from(new Set([...safeKeys(oldObj), ...safeKeys(newObj)]));
  let out: FieldChange[] = [];
  for (const k of keys) {
    if (IGNORE_KEYS.has(k)) continue;
    const hasOld = Object.prototype.hasOwnProperty.call(oldObj, k);
    const hasNew = Object.prototype.hasOwnProperty.call(newObj, k);
    const before = hasOld ? oldObj[k] : null;
    const after = hasNew ? newObj[k] : null;
    if (before == null && after == null) continue;
    if (!isEqual(before, after)) out.push({ key: k, before, after });
  }
  if (r.action === 'INSERT') {
    const tableSpecific = IGNORE_ON_INSERT_BY_TABLE[r.table_name] || new Set<string>();
    out = out.filter((c) => !(IGNORE_ON_INSERT_GLOBAL.has(c.key) || tableSpecific.has(c.key)));
  }
  return out;
}

function collapseTypingBursts(allRows: AuditLogRow[]): Map<number, FieldChange[]> {
  const rows = [...allRows].sort((a, b) => new Date(a.changed_at).getTime() - new Date(b.changed_at).getTime());
  const rowChanges = new Map<number, FieldChange[]>();
  for (const r of rows) rowChanges.set(r.id, computeFieldChanges(r));
  type Agg = { firstBefore: any; lastAfter: any; lastAt: number; lastRow: AuditLogRow; rowIds: number[] };
  const aggs = new Map<string, Agg>();
  const keyFor = (r: AuditLogRow, k: string) => `${r.table_name}:${r.record_id}:${r.changed_by || 'null'}:${k}`;
  for (const r of rows) {
    if (r.action !== 'UPDATE') continue;
    const t = new Date(r.changed_at).getTime();
    const changes = rowChanges.get(r.id) || [];
    for (const c of changes) {
      const gk = keyFor(r, c.key);
      const prev = aggs.get(gk);
      if (prev && t - prev.lastAt <= TYPING_MERGE_MS) {
        prev.lastAfter = c.after;
        prev.lastAt = t;
        prev.lastRow = r;
        prev.rowIds.push(r.id);
      } else {
        if (prev) {
          for (const rid of prev.rowIds) {
            const arr = rowChanges.get(rid) || [];
            rowChanges.set(rid, arr.filter((fc) => fc.key !== c.key));
          }
          const endId = prev.lastRow.id;
          const arrEnd = rowChanges.get(endId) || [];
          const filtered = arrEnd.filter((fc) => fc.key !== c.key);
          filtered.push({ key: c.key, before: prev.firstBefore, after: prev.lastAfter });
          rowChanges.set(endId, filtered);
        }
        aggs.set(gk, { firstBefore: c.before, lastAfter: c.after, lastAt: t, lastRow: r, rowIds: [r.id] });
      }
    }
  }
  for (const [gk, prev] of aggs) {
    const key = gk.split(':').slice(-1)[0];
    for (const rid of prev.rowIds) {
      const arr = rowChanges.get(rid) || [];
      rowChanges.set(rid, arr.filter((fc) => fc.key !== key));
    }
    const endId = prev.lastRow.id;
    const arrEnd = rowChanges.get(endId) || [];
    const filtered = arrEnd.filter((fc) => fc.key !== key);
    filtered.push({ key, before: prev.firstBefore, after: prev.lastAfter });
    rowChanges.set(endId, filtered);
  }
  return rowChanges;
}

export function formatVal(v: any): string {
  if (v == null) return '(empty)';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v.length === 0 ? '(empty)' : truncate(v, 80);
  try { return truncate(JSON.stringify(v), 80); } catch { return '[obj]'; }
}

function truncate(str: string, len: number): string {
  return str.length > len ? `${str.slice(0, len)}…` : str;
}

const ACTION_LABELS: Record<string, string> = {
  INSERT: 'Added', UPDATE: 'Updated', DELETE: 'Deleted', SOFT_DELETE: 'Marked deleted',
  RESTORE: 'Restored', RESEARCH_STATUS: 'Research status',
};

export default function RecordHistoryTab({
  auditRows,
  loading,
  error,
  extraDisplayItems,
  extraFilterOptions,
  filterPredicate,
  tableToTab = defaultTableToTab,
  resolveFieldLabel,
  storageKey,
  canUndo,
  undoMutation,
  userNames,
  userHeadshots,
  renderRowAction,
  resolveRowChanges,
  renderFieldValue,
  formatFieldPreview,
  title = 'History',
  onFiltersChange,
  tableOptions,
  actionOptions,
  hasMore,
  loadingMore,
  onLoadMore,
  presentation = 'table',
  refreshing = false,
  onRetry,
}: RecordHistoryTabProps) {
  const [filterTable, setFilterTable] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const composing = useRef(false);
  const committedSearch = useRef('');
  const isVenue = presentation === 'venue';

  const changeFilters = (next: Partial<VenueHistoryFilters>) => {
    const filters = { search: committedSearch.current, table: filterTable, action: filterAction, ...next };
    committedSearch.current = filters.search;
    setSearchText(filters.search);
    setFilterTable(filters.table);
    setFilterAction(filters.action);
    onFiltersChange?.(filters);
  };

  const labelField = resolveFieldLabel || humanizeField;

  const filteredRows = useMemo(() => {
    let data = auditRows;
    if (filterTable) data = data.filter((r) => r.table_name === filterTable);
    if (filterAction) data = data.filter((r) => r.action === filterAction);
    return data;
  }, [auditRows, filterTable, filterAction]);

  const auditDisplayItems = useMemo<DisplayItem[]>(() => {
    const map = collapseTypingBursts(filteredRows);
    const items: DisplayItem[] = [];
    for (const r of filteredRows) {
      const changes = resolveRowChanges?.(r) ?? map.get(r.id) ?? [];
      if (r.action === 'DELETE') items.push({ row: r, changes });
      // Empty compact payloads still represent real activity. Rows whose saved
      // changes were merged into a later autosave must not become empty entries.
      else if (changes.length > 0 || (r.payload_mode === 'compact' && computeFieldChanges(r).length === 0)) items.push({ row: r, changes });
    }
    return filterTable ? items.filter((i) => i.row.table_name === filterTable) : items;
  }, [filteredRows, filterTable, resolveRowChanges]);

  // Check if the current filter is an extra filter (not a DB table)
  const extraFilterValues = useMemo(
    () => new Set((extraFilterOptions || []).map((o) => o.value)),
    [extraFilterOptions],
  );
  const isExtraFilter = filterTable && extraFilterValues.has(filterTable);

  const displayItems = useMemo<DisplayItem[]>(() => {
    // When filtering to an extra filter, show only matching extra items
    if (isExtraFilter && filterPredicate) {
      const matched = (extraDisplayItems || []).filter((item) => filterPredicate(item, filterTable));
      return matched.sort((a, b) => new Date(b.row.changed_at).getTime() - new Date(a.row.changed_at).getTime());
    }
    // When filtering to a DB table, don't include extra items
    if (filterTable && !isExtraFilter) {
      return auditDisplayItems.sort((a, b) => new Date(b.row.changed_at).getTime() - new Date(a.row.changed_at).getTime());
    }
    // No filter: merge audit items + extra items
    const merged = [...auditDisplayItems, ...(extraDisplayItems || [])];
    const sorted = merged.sort((a, b) => new Date(b.row.changed_at).getTime() - new Date(a.row.changed_at).getTime());

    // Deduplicate visually identical rows that happen at the same second
    const DEDUP_MS = 2000;
    const deduped: DisplayItem[] = [];
    const seen = new Set<string>();
    for (const item of sorted) {
      const r = item.row;
      const changesKey = item.changes
        .map((c) => `${c.key}:${formatVal(c.before)}:${formatVal(c.after)}`)
        .sort()
        .join('|');
      const timeBucket = Math.floor(new Date(r.changed_at).getTime() / DEDUP_MS);
      const dedupKey = `${r.table_name}:${r.record_id}:${r.action}:${r.changed_by || 'null'}:${timeBucket}:${changesKey}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      deduped.push(item);
    }
    return deduped;
  }, [auditDisplayItems, extraDisplayItems, filterTable, isExtraFilter, filterPredicate]);

  // Apply text search
  const searchedItems = useMemo(() => {
    if (onFiltersChange || !searchText.trim()) return displayItems;
    const q = searchText.toLowerCase();
    return displayItems.filter(({ row: r, changes }) => {
      if (r.action.toLowerCase().includes(q)) return true;
      if (r.table_name.toLowerCase().includes(q)) return true;
      if (tableToTab(r.table_name).toLowerCase().includes(q)) return true;
      const uName = auditActorLabel(r, userNames);
      if (uName.toLowerCase().includes(q)) return true;
      if (r.record_label?.toLowerCase().includes(q)) return true;
      for (const c of changes) {
        if (labelField(c.key, r).toLowerCase().includes(q)) return true;
        if (c.key.toLowerCase().includes(q)) return true;
        if (formatVal(c.before).toLowerCase().includes(q)) return true;
        if (formatVal(c.after).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [displayItems, searchText, userNames, tableToTab, labelField, onFiltersChange]);

  const uniqueActions = useMemo(() => [...new Set(auditRows.map((r) => r.action))], [auditRows]);
  const uniqueTables = useMemo(() => [...new Set(auditRows.map((r) => r.table_name))], [auditRows]);
  const hasFilters = !!(searchText || filterTable || filterAction);
  const resetFilters = () => {
    changeFilters({ search: '', table: '', action: '' });
    searchRef.current?.focus();
  };
  const countLabel = onLoadMore
    ? `${searchedItems.length} entries shown${hasMore ? ' · More available' : ''}`
    : `${searchedItems.length} of ${displayItems.length} entries`;

  return (
    <div className={`stack gap-sm record-history${isVenue ? ' record-history--venue' : ''}`}>
      <div className="record-history__heading">
        <h2 className="m0">{title}</h2>
      </div>
      <div className="library-toolbar-block">
        <div className={isVenue ? "library-toolbar" : "card soft library-toolbar"} role={isVenue ? "search" : "toolbar"} aria-label="History filters and actions">
          <div className="library-toolbar__search">
            <div className="library-toolbar__search-wrap">
              <input
                ref={searchRef}
                className="search-input"
                placeholder={isVenue ? 'Search people or changes…' : 'Search history…'}
                value={searchText}
                onCompositionStart={() => { composing.current = true; }}
                onCompositionEnd={(e) => { composing.current = false; changeFilters({ search: e.currentTarget.value }); }}
                onChange={(e) => {
                  if (composing.current) setSearchText(e.target.value);
                  else changeFilters({ search: e.target.value });
                }}
                aria-label="Search history"
              />
              {searchText.length > 0 && (
                <button
                  type="button"
                  className="library-toolbar__search-clear"
                  onClick={() => { changeFilters({ search: '' }); searchRef.current?.focus(); }}
                  aria-label="Clear search"
                  title="Clear search"
                >✕</button>
              )}
            </div>
          </div>
          <div className="library-toolbar__filters">
            <label className="library-toolbar__field text-xxs fg-muted">
              <span className={isVenue ? 'sr-only' : undefined}>{isVenue ? 'Area' : 'Table'}</span>
              <select className="xs" value={filterTable} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => changeFilters({ table: e.target.value })}>
                <option value="">{isVenue ? 'All areas' : 'All'}</option>
                {(extraFilterOptions || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
                {(tableOptions ?? uniqueTables.map(t => ({ value: t, label: t }))).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="library-toolbar__field text-xxs fg-muted">
              <span className={isVenue ? 'sr-only' : undefined}>Action</span>
              <select className="xs" value={filterAction} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => changeFilters({ action: e.target.value })}>
                <option value="">{isVenue ? 'All actions' : 'All'}</option>
                {(actionOptions ?? uniqueActions).map((a) => (
                  <option key={a} value={a}>{isVenue ? ACTION_LABELS[a] || humanizeField(a.toLowerCase()) : a}</option>
                ))}
              </select>
            </label>
          </div>
          {isVenue && hasFilters && <button type="button" className="btn ghost small record-history__reset" onClick={resetFilters}>Reset filters</button>}
          {!isVenue && (
          <div className="library-toolbar__meta">
            {loading && <span className="text-sm fg-muted" role="status" aria-live="polite">Loading...</span>}
            <span className="lib-count">{countLabel}</span>
          </div>
          )}
        </div>
      </div>
      {error && <div className="alert alert-error record-history__error" role="alert">
        <span>{error}</span>
        {onRetry && <button type="button" className="btn small" disabled={loading || refreshing || loadingMore} onClick={onRetry}>Try again</button>}
      </div>}
      {isVenue ? (
        <VenueActivityFeed items={searchedItems} storageKey={storageKey} userNames={userNames} userHeadshots={userHeadshots}
          labelField={labelField} tableToTab={tableToTab} actionLabel={action => ACTION_LABELS[action] || humanizeField(action.toLowerCase())}
          status={loading ? 'Loading history…' : `${countLabel}${refreshing ? ' · Refreshing…' : ''}`}
          busy={loading || refreshing || !!loadingMore} canUndo={canUndo} undoMutation={undoMutation} renderRowAction={renderRowAction}
          renderFieldValue={renderFieldValue} formatFieldPreview={formatFieldPreview} />
      ) : (
      <CardableTable storageKey={storageKey} className="record-history-table" tableWrapperClassName="audit-wrap">
        <table className="data-table" aria-label="Change history" aria-busy={loading || refreshing || loadingMore}>
          <thead>
            <tr>
              <th className="vh-col-action">Action</th>
              <th className="vh-col-tab">Tab</th>
              <th className="vh-col-user">User / source</th>
              <th className="vh-col-changes">Changes</th>
              <th className="vh-col-when">When</th>
              <th className="vh-col-undo"></th>
            </tr>
          </thead>
          <tbody>
            {searchedItems.map(({ row: r, changes }) => {
              const userDisplay = auditActorLabel(r, userNames);

              // Use custom row action renderer if provided, otherwise default undo button
              let actionCell: React.ReactNode = null;
              if (renderRowAction) {
                actionCell = renderRowAction({ row: r, changes });
              } else if (canUndo) {
                const rowUndoable = isAuditRowUndoable(r);
                actionCell = (
                  <button
                    type="button"
                    className="btn micro"
                    onClick={() => undoMutation.mutate(r)}
                    disabled={undoMutation.isPending || !rowUndoable}
                    aria-label="Undo this change"
                    title={rowUndoable ? 'Undo this change' : 'Undo unavailable for compacted history entries'}
                    style={{ minWidth: '32px', padding: '0.25rem 0.5rem' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
                  </button>
                );
              }

              return (
                <tr key={r.id}>
                  <td className="vh-col-action">
                    <span className={`pill micro ${r.action === 'DELETE' ? 'warn' : ''}`}>{r.action}</span>
                  </td>
                  <td className="vh-col-tab">{tableToTab(r.table_name)}</td>
                  <td className="vh-col-user" title={userDisplay}><span className="user-identity">
                    <UserAvatar name={userDisplay} size={24} src={r.changed_by && (!r.activity_source || r.activity_source === 'user') ? userHeadshots?.[r.changed_by] : undefined}
                      fallback={r.activity_source === 'ai' ? 'AI' : r.activity_source === 'import' ? 'IM' : !r.changed_by ? 'SY' : undefined} />
                    <span>{userDisplay}</span>
                  </span></td>
                  <td className="vh-col-changes">
                    {r.record_label && <div className="text-xs">{r.record_label}</div>}
                    {r.payload_mode === 'compact' && (
                      <div className="text-xxs fg-muted" style={{ marginBottom: changes.length > 0 ? '.35rem' : 0 }}>
                        {changes.length > 0 ? 'Saved field changes' : 'Change recorded; full details were not retained'}
                      </div>
                    )}
                    {r.action === 'DELETE' && <span className="text-xs fg-warn">Record deleted</span>}
                    {changes.length > 0 && (
                      <div className="venue-history-changes">
                        {changes.map((c) => (
                          <div key={c.key} className="venue-history-change">
                            <span className="venue-history-change__field">{labelField(c.key, r)}</span>
                            <span className="venue-history-change__values">
                              <span className="venue-history-change__before" title={String(c.before)}>{formatVal(c.before)}</span>
                              <span className="venue-history-change__arrow">&rarr;</span>
                              <span className="venue-history-change__after" title={String(c.after)}>{formatVal(c.after)}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="vh-col-when">{new Date(r.changed_at).toLocaleString()}</td>
                  <td className="vh-col-undo">{actionCell}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardableTable>
      )}
      {hasMore && onLoadMore && (
        <button className="btn small record-history__load-more" type="button" disabled={loadingMore || loading} onClick={onLoadMore}>
          {loadingMore ? 'Loading older history…' : 'Load older history'}
        </button>
      )}
      {searchedItems.length === 0 && !loading && !error && (isVenue ? <div className="record-history__empty">
        <h3>{hasFilters ? 'No matching activity' : 'No history yet'}</h3>
        <p>{hasFilters ? 'Try another name or change, or reset your filters.' : 'Recorded changes to this venue will appear here.'}</p>
        {hasFilters && <button type="button" className="btn small" onClick={resetFilters}>Clear all filters</button>}
      </div> : <p className="fg-muted text-sm">No history entries match your filters.</p>)}
    </div>
  );
}
